import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment as env } from 'src/environments/environment';
import { AuthService } from './http-services/authservice.service';
import { lastValueFrom, Observable, Subject, throwError } from 'rxjs';
import { GenerateSEBConfigModel, IDailyProctorScheduleModel, IExam, IExamCategory, IExaminationExaminer, IExaminationGroup, IExamInstruction, IExamPenalty, IQuestionCategory, IExamQuestions, IResultReport, IScheduleCandidate, SaqCandidateQuestionModel, IExamQuestionBank, IQuestionGroup, SaqCandidateQuestionResponseModel, IExaminationReviewer, SaqCandidateResponseModel, MarkingQuestionModel } from '../interface/dto';
import { TableParams } from '../components/grid/TableParams';
import { ITableParams } from '../components/grid/ITableParams';
import { th } from 'date-fns/locale';
import { ServiceResponse } from '../models/service-response';
import { LoadingInterceptor } from '../interceptors/http-interceptors/loading.interceptor';
import { LoaderService } from './loader-services/loader.service';
import { SnackbarService } from './snackbar.service';
import { IFilterParam } from '../components/grid/IFilterParam';

@Injectable({
  providedIn: 'root'
})
export class ExamService {

  constructor(private httpClient: HttpClient, private auth: AuthService,
     private loaderService: LoaderService, private snackbar: SnackbarService) { }
  reloadGrid = new Subject<any>();
  ExamId: Subject<number> = new Subject();
  private _selectAll: boolean = false;
  private _filterParams: IFilterParam[] = [];
  public get filterParams(): IFilterParam[] {
    return this._filterParams;
  }
  public set filterParams(value: IFilterParam[]) {
    this._filterParams = value;
  }

  configureHeaders() {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', `Bearer ${this.auth.token}`);
    return headers;
  }

  get selectedExamIds() {
    if (this.selectedExams == null || this.selectedExams.length === 0) {
      return [];
    }
    return this.selectedExams.map(x => x.id);
  }
  public get selectAll(): boolean {
    return this._selectAll;
  }
  public set selectAll(value: boolean) {
    this._selectAll = value;
  }
  setSelectedExam(exams: IExam[]) {
    localStorage.setItem("selectedExams", JSON.stringify(exams));
  }

  get selectedExams() {
    return JSON.parse(localStorage.getItem("selectedExams")) as IExam[];
  }

  setSelectedExamGroup(examGroup: IExaminationGroup[]) {
    localStorage.setItem("selectedExamGroup", JSON.stringify(examGroup));
  }
  setSelectedQuestionGroup(questionGroup: IQuestionGroup[]) {
    localStorage.setItem("selectedQuestionGroup", JSON.stringify(questionGroup));
  }

  get selectedExamGroups() {
    return JSON.parse(localStorage.getItem("selectedExamGroup")) as IExaminationGroup[];
  }
  get selectedQuestionGroups() {
    return JSON.parse(localStorage.getItem("selectedQuestionGroup")) as IQuestionGroup[];
  }
  get selectedExamGroupIds() {
    if (this.selectedExamGroups == null || this.selectedExamGroups.length === 0) {
      return 0;
    }
    return this.selectedExamGroups.map(x => x.examinationGroupID);
  }

  get selectedQuestionGroupIds() {
    if (this.selectedQuestionGroups == null || this.selectedQuestionGroups.length === 0) {
      return 0;
    }
    return this.selectedQuestionGroups.map(x => x.id);
  }
  get selectedQuestionsIds() {
    if (this.selectedQuestions == null || this.selectedQuestions.length === 0) {
      return 0;
    }
    return this.selectedQuestions.map(x => x.examinationQuestionID);
  }

  setSelectedQuestion(questions: IExamQuestions[]) {
    localStorage.setItem("selectedQuestions", JSON.stringify(questions));
  }

  setSelectedExamQuestionBank(questionBanks: IExamQuestionBank[]) {
    sessionStorage.setItem("selectedExamQuestionBanks", JSON.stringify(questionBanks));
  }

  get selectedQuestions() {
    return JSON.parse(localStorage.getItem("selectedQuestions")) as IExamQuestions[];
  }

  async getExamination(tableParam: TableParams) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examination/`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }

  async getExtraFieldsLabel(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examination/extrafieldslabel/`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }

  async getEssayExamLog(tableParam) {
    //const tParams: ITableParams = tableParam.value;
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examination/essayexamlog/`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }

  async getExamSchedules() {
    return lastValueFrom(this.httpClient.get(`${env.apiURL}examination/${this.selectedExamIds}/schedules`,
      { headers: this.configureHeaders() }), { defaultValue: [] });
  }
  getExamById(examID: any) {
    return this.httpClient.get(`${env.apiURL}examination/${examID}`, { headers: this.configureHeaders() }).toPromise();

  }
  async getExamRegURL(id: number) {
    return this.httpClient.get(`${env.apiURL}examination/examurl/${id}`, { headers: this.configureHeaders() }).toPromise();
  }

  async uploadExamQuestions(data: FormData) {
    let header = new HttpHeaders({
      'Content-Disposition': 'multipart/form-data',
      "Authorization": `Bearer ${this.auth.token}`
    });

    const result = await this.handleBackgroundTaskRequest(this.httpClient.post(`${env.apiURL}examination/questions/upload`, data, { headers: header }));
    return result;
  }

  async getAllExams() {
    return this.httpClient.get(`${env.apiURL}examination/`, { headers: this.configureHeaders() }).toPromise();
  }

  async copyExam(data) {
    return this.handleBackgroundTaskRequest(this.httpClient.post(`${env.apiURL}examination/questions/copy/`,data, { headers: this.configureHeaders() }));
  }

  async pauseUnpauseExam(data) {
    return this.httpClient.put(`${env.apiURL}examination/pausestatus`, data, { headers: this.configureHeaders() }).toPromise();
  }

  async generateDPS(dpsData: IDailyProctorScheduleModel) {
    return this.httpClient.post(`${env.apiURL}examination/generatedps`, dpsData, { headers: this.configureHeaders() }).toPromise();
  }

  async createExam(examData: FormData) {
    let header = new HttpHeaders({
      'Content-Disposition': 'multipart/form-data',
      "Authorization": `Bearer ${this.auth.token}`
    });
    return this.handleBackgroundTaskRequest(this.httpClient.post(`${env.apiURL}examination/`, examData, { headers: header }));
  }

  async updateExam(examData: FormData) {
    let header = new HttpHeaders({
      'Content-Disposition': 'multipart/form-data',
      "Authorization": `Bearer ${this.auth.token}`
    });
    return this.handleBackgroundTaskRequest(this.httpClient.put(`${env.apiURL}examination/`, examData, { headers: header }));
  }

  async deleteExam(examData) {
    return this.httpClient.delete(`${env.apiURL}examination`, { headers: this.configureHeaders(), body: examData }).toPromise();
  }

  // async processExam(examIds, process) {
  //   return this.httpClient.get(`${env.apiURL}examination/questions/preload/${process}?${examIds}`, { headers: this.configureHeaders() }).toPromise();
  // }

  async testExam(data) {
    return this.httpClient.post(`${env.apiURL}examination/questions/test`, data, { headers: this.configureHeaders() }).toPromise();
  }

  async exportQuest(examId, format, category) {
    return this.httpClient.get(`${env.apiURL}examination/${examId}/questions/export?category=${category}&format=${format}&examinationID=${examId}`, { headers: this.configureHeaders() }).toPromise();
  }

  async getExamQuestions(tableParam: ITableParams) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examination/questions`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }

  async deleteQuestion(questionData) {
    return this.httpClient.delete(`${env.apiURL}examination/questions`, { headers: this.configureHeaders(), body: questionData }).toPromise();
  }

  async getQuestion(questionId) {
    return this.httpClient.get(`${env.apiURL}examination/question/${questionId}`, { headers: this.configureHeaders() }).toPromise();
  }

  async updateQuestion(questionRequest): Promise<ServiceResponse> {
    return this.httpClient.put<ServiceResponse>(`${env.apiURL}examination/questions`, questionRequest, { headers: this.configureHeaders() }).toPromise();
  }
  async saveQuestion(questionRequest): Promise<ServiceResponse> {
    return this.httpClient.post<ServiceResponse>(`${env.apiURL}examination/questions`, questionRequest, { headers: this.configureHeaders() }).toPromise();
  }

  async changeQuestionStatus(questionId) {
    return this.httpClient.post(`${env.apiURL}examination/questions/disabled`, questionId, { headers: this.configureHeaders() }).toPromise();
  }

  async getExamScriptReport(examId, resultData) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/${examId}?${resultData}`, { headers: this.configureHeaders() }));
  }

  async getExamFeedbackReport(examId, value) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/${examId}/${value}`, { headers: this.configureHeaders() }));
  }

  async getExamQuestionReport(resultData) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/question?${resultData}`, { headers: this.configureHeaders() }));
  }

  async getExamResultReport(resultData: any) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/result?${resultData}`, { headers: this.configureHeaders() }));
  }

  async getExamResultCardReport(resultData: any) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/result/card?${resultData}`, { headers: this.configureHeaders() }));
  }
  async getExamResultCategoryReport(resultData: any) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/result/category?${resultData}`, { headers: this.configureHeaders() }));
  }

  async getCompanyQuestions(data) {
    return this.httpClient.get(`${env.apiURL}examination/${data}/questions`, { headers: this.configureHeaders() }).toPromise();
  }

  async candidateSchedule(schedule: IScheduleCandidate) {
    return this.httpClient.post(`${env.apiURL}examination/candidateschedule`, schedule, { headers: this.configureHeaders() }).toPromise();
  }

  async uploadCandidate(data: FormData) {
    let header = new HttpHeaders({
      'Content-Disposition': 'multipart/form-data',
      "Authorization": `Bearer ${this.auth.token}`
    });
    return this.handleBackgroundTaskRequest(this.httpClient.post(`${env.apiURL}examination/candidate/upload`, data, { headers: header }));
  }

  async getExamCategoryTable(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examination/category`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }
  async getExamCategories(examId) {
    return this.httpClient.get(`${env.apiURL}examination/category?examId=${examId}`, { headers: this.configureHeaders() }).toPromise();
  }
  async createExamCategory(data: IExamCategory) {
    return this.httpClient.post(`${env.apiURL}examination/category/create`, data, { headers: this.configureHeaders() }).toPromise();
  }

  async updateExamCategory(data: IExamCategory) {
    return this.httpClient.put(`${env.apiURL}examination/category/update`, data, { headers: this.configureHeaders() }).toPromise();
  }

  async deleteExamCategory(data: IExamCategory) {
    return this.httpClient.delete(`${env.apiURL}examination/category/delete?category=${data.category}&examId=${data.examinationID}`, { headers: this.configureHeaders() }).toPromise();
  }

  async getDashboardData() {
    return lastValueFrom(this.httpClient.get(`${env.apiURL}examination/dashboard`,
      { headers: this.configureHeaders() }), { defaultValue: [] });
  }

  async getExaminationExport(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examination/export`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }

  async getQuestionExport(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examination/questions/export`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }

  async updateExamPenalties(examPenalty: IExamPenalty) {
    return await lastValueFrom(this.httpClient.put(
      `${env.apiURL}examination/${this.selectedExamIds[0]}/penalties`,
      examPenalty, { headers: this.configureHeaders() }
    ), { defaultValue: false });
  }

  async getExamPenalties() {
    return lastValueFrom(this.httpClient.get(`${env.apiURL}examination/${this.selectedExamIds[0]}/penalties`,
      { headers: this.configureHeaders() }), { defaultValue: {} });
  }

  async getSEBInstructionUrl() {
    return lastValueFrom(this.httpClient.get(`${env.apiURL}examination/${this.selectedExamIds[0]}/sebinstructionpageurl`,
      { headers: this.configureHeaders() }), { defaultValue: {} });
  }

  async generateSEBConfig(data: GenerateSEBConfigModel) {
    return this.handleBackgroundTaskRequest(this.httpClient.post(`${env.apiURL}examination/generatesebconfig`, data, { headers: this.configureHeaders() }));
  }

  async getExamList() {
    return lastValueFrom(this.httpClient.get(`${env.apiURL}examination/list`,
      { headers: this.configureHeaders() }), { defaultValue: {} });
  }
  async autoMarkSAQ(data: any) {
    return this.handleBackgroundTaskRequest(this.httpClient.post(`${env.apiURL}examination/automarksaq`, data, { headers: this.configureHeaders() }));
  }
  async importCandidatesData(data: any) {
    return this.handleBackgroundTaskRequest(this.httpClient.post(`${env.apiURL}examination/imports3data`, data, { headers: this.configureHeaders() }));
  }
  async getEssayCandidate(data: any) {
    return lastValueFrom(this.httpClient.patch(`${env.apiURL}examination/essaycandidate`, data, { headers: this.configureHeaders() }), { defaultValue: [] });
  }
  async getMarkEssayQuestions(data: any) {
    return lastValueFrom(this.httpClient.patch(`${env.apiURL}examination/essayquestions`, data, { headers: this.configureHeaders() }), { defaultValue: [] });
  }
  async markEssayQuestion(data: any) {
    return lastValueFrom(this.httpClient.patch(`${env.apiURL}examination/markessayquestion`, data, { headers: this.configureHeaders() }), { defaultValue: [] });
  }
  async getMarkSAQQuestions(data: any): Promise<SaqCandidateQuestionResponseModel> {
    return lastValueFrom(this.httpClient.patch<SaqCandidateQuestionResponseModel>(`${env.apiURL}examination/saqquestion`, data, { headers: this.configureHeaders() }),
     { defaultValue: { saqCandidates: [],
        candidateCount: 0, candidateMarkedCount: 0, candidateUnmarkedCount: 0} });
  }

  async getSaqCandidates(data: any): Promise<SaqCandidateResponseModel> {
    return lastValueFrom(
      this.httpClient.patch<SaqCandidateResponseModel>(
        `${env.apiURL}examination/saqcandidates`,
        data,
        { headers: this.configureHeaders() }
      ),
      { defaultValue: { saqCandidates: [], candidateCount: 0, candidateMarkedCount: 0, candidateUnmarkedCount: 0} }
    );
  }

  async getSaqQuestions(data: any): Promise<MarkingQuestionModel[]> {
    return lastValueFrom(
      this.httpClient.patch<MarkingQuestionModel[]>(
        `${env.apiURL}examination/saqquestions`,
        data,
        { headers: this.configureHeaders() }
      ),
      { defaultValue: [] }
    );
  }

  async markSAQQuestion(data: any) {
    return lastValueFrom(this.httpClient.patch(`${env.apiURL}examination/marksaqquestion`, data, { headers: this.configureHeaders() }), { defaultValue: [] });
  }

  async adjustQuestionScore(data: any) {
    return lastValueFrom(this.httpClient.patch(`${env.apiURL}examination/adjustquestionscore`, data, { headers: this.configureHeaders() }), { defaultValue: [] });
  }
  async uploadExamQuestionsImage(data: FormData) {
    let header = new HttpHeaders({
      'Content-Disposition': 'multipart/form-data',
      "Authorization": `Bearer ${this.auth.token}`
    });
    return this.httpClient.post(`${env.apiURL}examination/questions/uploadquestionimage`, data, { headers: header }).toPromise();
  }
  async publishQuestions(data) {
    return this.handleBackgroundTaskRequest(this.httpClient.post(`${env.apiURL}examination/questions/publishquestions`, data, { headers: this.configureHeaders() }));
  }
  async setUpDefaultProctor(examId: any) {
    return this.httpClient.get(`${env.apiURL}examination/questions/defaultproctor?examinationID=${examId}`, { headers: this.configureHeaders() }).toPromise();
  }
  async uploadMarkSheet(data: FormData) {
    let header = new HttpHeaders({
      'Content-Disposition': 'multipart/form-data',
      "Authorization": `Bearer ${this.auth.token}`
    });
    return this.handleBackgroundTaskRequest(this.httpClient.post(`${env.apiURL}examination/uploadmarksheet`, data, { headers: header }));
  }
  async assignSchedulesExaminer(data: any) {
    return lastValueFrom(this.httpClient.post(`${env.apiURL}examination/assignschedulesexaminer`, data, { headers: this.configureHeaders() }));
  }
  async generateMarkSheet(resultData: any) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/generatemarksheet?${resultData}`, { headers: this.configureHeaders() }));
  }
  async examScoreByIP(resultData) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/examscorebyip?${resultData}`, { headers: this.configureHeaders() }));
  }
  async examScoreByMAC(resultData) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/examscorebymac?${resultData}`, { headers: this.configureHeaders() }));
  }

  async getExamInstructions(examId) {
    return this.httpClient.get(`${env.apiURL}examination/examinstruction?examId=${examId}`, { headers: this.configureHeaders() }).toPromise();
  }
  saveExamInstructions(examInstruction: IExamInstruction) {
    return lastValueFrom(this.httpClient.post(`${env.apiURL}examination/saveexaminstruction`, examInstruction, { headers: this.configureHeaders() }));
  }
  async getQuestionCategories() {
    return lastValueFrom(this.httpClient.get(`${env.apiURL}examination/questioncategory`, { headers: this.configureHeaders() }), { defaultValue: [] });
  }
  async getQuestionCategoryTable(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examination/questioncategory`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }
  async saveQuestionCategory(data: IQuestionCategory) {
    return this.httpClient.post(`${env.apiURL}examination/questioncategory`, data, { headers: this.configureHeaders() }).toPromise();
  }

  async deleteQuestionCategory(data: IQuestionCategory) {
    return this.httpClient.delete(`${env.apiURL}examination/questioncategory?id=${data.id}`, { headers: this.configureHeaders() }).toPromise();
  }

  async submitSaqCandidate(data: any) {
    return this.httpClient.post(`${env.apiURL}examination/submitsaqcandidate`, data, { headers: this.configureHeaders() }).toPromise();
  }

  async setEssayAsMarked(examinationId: number, examinationScheduleId: number) {
    return lastValueFrom(this.httpClient.post(`${env.apiURL}examination/${examinationId}/setessayasmarked/${examinationScheduleId}`, {}, { headers: this.configureHeaders() }), { defaultValue: [] });
  }

  async setAsReviewed(examinationId: number, examinationScheduleId: number) {
    return lastValueFrom(this.httpClient.post(`${env.apiURL}examination/${examinationId}/setessayasreviewed/${examinationScheduleId}`, {}, { headers: this.configureHeaders() }), { defaultValue: [] });
  }

  async deleteExaminationExaminer(data: IExaminationExaminer) {
    return this.httpClient.delete(`${env.apiURL}examination/examexaminer?id=${data.id}`, { headers: this.configureHeaders() }).toPromise();
  }
  async saveExaminationExaminer(data: IExaminationExaminer) {
    return this.httpClient.post(`${env.apiURL}examination/examexaminer`, data, { headers: this.configureHeaders() }).toPromise();
  }
  async getExaminationExaminerTable(tableParam) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examination/examexaminer`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }

  async deleteExaminationReviewer(data: IExaminationReviewer) {
    return this.httpClient.delete(
      `${env.apiURL}examination/examreviewer/${data.id}`,
      { headers: this.configureHeaders() }
    ).toPromise();
  }

  async saveExaminationReviewer(data: IExaminationReviewer) {
    return this.httpClient.post(
      `${env.apiURL}examination/examreviewer`,
      data,
      { headers: this.configureHeaders() }
    ).toPromise();
  }

  async getExaminationReviewerTable(tableParam: any) {
    return await lastValueFrom(
      this.httpClient.patch(
        `${env.apiURL}examination/examreviewer`,
        tableParam,
        { headers: this.configureHeaders() }
      ),
      { defaultValue: [] }
    );
  }
  async getUsers() {
    return lastValueFrom(this.httpClient.get(`${env.apiURL}examination/users`, { headers: this.configureHeaders() }), { defaultValue: [] });
  }

  async getSaqQuestionWithUniqueAns(questionId) {
    return this.httpClient.get(`${env.apiURL}examination/question/saquniqueanswers/${questionId}`, { headers: this.configureHeaders() }).toPromise();
  }

  async getExamGroups(tableParam: TableParams) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examination/exam-groups`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }

  async saveExamGroup(data: IExaminationGroup) {
    return this.httpClient.post(`${env.apiURL}examination/exam-groups`, data, { headers: this.configureHeaders() }).toPromise();
  }

  async deleteExamGroup(data: IExaminationGroup) {
    return this.httpClient.delete(`${env.apiURL}examination/exam-groups?id=${data.id}`, { headers: this.configureHeaders() }).toPromise();
  }

  async getExamGroupsLookup(): Promise<IExaminationGroup[]> {
    return this.httpClient
      .get<IExaminationGroup[]>(`${env.apiURL}examination/exam-groups-lookup`, {
        headers: this.configureHeaders()
      })
      .toPromise();
  }

  async changeExamGroup(examGroupId: string, examId: number) {
    return this.httpClient.post(`${env.apiURL}examination/${examId}/exam-groups`, { examinationGroupID: examGroupId ?? "" }, { headers: this.configureHeaders() }).toPromise();
  }
  async getExaminersMarkingReport(resultData) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/examinersmarkingreport?${resultData}`, { headers: this.configureHeaders() }));
  }
  async getExaminersReviewReport(resultData) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/examinersreviewreport?${resultData}`, { headers: this.configureHeaders() }));
  }
  async getExamMarkingSummary(resultData) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/exammarkingsummary?${resultData}`, { headers: this.configureHeaders() }));
  }
  async getExamScriptHtmlContent(examId, scheduleId, showUnansweredSAQ, excludeTestCandidates) {
      return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/getexamscripthtml?examId=${examId}&scheduleId=${scheduleId}&showOnlyAnsweredSAQ=${showUnansweredSAQ}&excludeTestCandidates=${excludeTestCandidates}`, { headers: this.configureHeaders() }));
  }
  async getQuestionAnalysisHtmlContent(examId) {
    return this.httpClient.get(`${env.apiURL}examination/getquestionanalysishtml?examId=${examId}`, { headers: this.configureHeaders() }).toPromise();
  }

  async getQuestionAnalysisExcel(examId) {
    const result = await this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/getquestionanalysisexcel?examId=${examId}`, { headers: this.configureHeaders() }));
    return result;
  }

  async getQuestionCategoriesLookup(examType: string) {
    return lastValueFrom(this.httpClient.get(`${env.apiURL}examination/questioncategorieslookup&examType=${examType}`, { headers: this.configureHeaders() }), { defaultValue: [] });
  }

  async replaceQuestionFromQuestionBank(examQuestionID: number) {
    return (this.httpClient.post(`${env.apiURL}examination/questions/${examQuestionID}/replace`, {}, { headers: this.configureHeaders() })).toPromise();
  }

  async getExaminationQuestionBanks(tableParams: any): Promise<IExamQuestionBank[]> {
    return await this.httpClient.post<IExamQuestionBank[]>(
      `${env.apiURL}examination/examquestionbanks`,
      tableParams, { headers: this.configureHeaders() }
    ).toPromise();
  }

  async getExaminationQuestionBank(examID: number, examQuestionBankID: number): Promise<IExamQuestionBank> {
    return await this.httpClient.get<IExamQuestionBank>(
      `${env.apiURL}examination/${examID}/examquestionbank/${examQuestionBankID}`, { headers: this.configureHeaders() }
    ).toPromise();
  }

  async saveExaminationQuestionBank(questionBankRequest: any): Promise<any> {
    return await this.httpClient.post<any>(
      `${env.apiURL}examination/createexamquestionbank`,
      questionBankRequest, { headers: this.configureHeaders() }
    ).toPromise();
  }

  async updateExaminationQuestionBank(questionBankRequest: any): Promise<any> {
    return await this.httpClient.put<any>(
      `${env.apiURL}examination/updateexamquestionbank`,
      questionBankRequest, { headers: this.configureHeaders() }
    ).toPromise();
  }

  
  async handleBackgroundTaskRequest(request: Observable<any>) {
    this.loaderService.setLoading(true);
    this.loaderService.setProgress(0);
    try {
      // console.log('handleBackgroundTaskRequest: Starting background task request');
      const res = await lastValueFrom(request);
      // console.log('handleBackgroundTaskRequest: Received response', res);
      
      // Handle case where batchInfoId might be in res or res.data
      const batchInfoId = res?.batchInfoId || res?.data?.batchInfoId;
      
      if (!batchInfoId || batchInfoId === 0) {
        // console.log('handleBackgroundTaskRequest: No batchInfoId, returning response directly');
        return res;
      }
        
        // console.log('handleBackgroundTaskRequest: Starting polling for batchInfoId:', res.batchInfoId);
        var batchResult = await this.pollForBatchTask(res.batchInfoId);
        if(batchResult?.fileName)
        {
          batchResult.fileName = res.fileName ? res.fileName : batchResult.fileName;
        } // preserve original file name returned;
        
        return batchResult;
    } catch (error) {
        console.error("handleBackgroundTaskRequest: Problem running task in the background", error);
        throw error;
    } finally {
        this.loaderService.setLoading(false);
    }
  }
    
  private async pollForBatchTask(batchInfoId: number, maxRetries: number = 100, pollInterval: number = 3000) {
    const throwTaskFailedError = (reason: string) => {
      throw new Error(reason);
    }
    // console.log('pollForBatchTask: Starting polling for batchInfoId:', batchInfoId);
    let unsuccessfulPollCount = 0;
    let succes = false
    let infologIndex = 0;
    while (unsuccessfulPollCount < maxRetries && !succes) {
      
      // console.log(`pollForBatchTask: Poll attempt #${pollCount}/${maxRetries} for batchInfoId:`, batchInfoId);
      
      try {
        const res = await lastValueFrom(
          this.httpClient.get<any>(
          `${env.apiURL}batchservice/batchinfostatus/${batchInfoId}`, 
          { headers: this.configureHeaders().set('manual_loading', 'true') }
          )
        );
        // console.log(`pollForBatchTask: Poll #${pollCount} response:`, res);
        
        // Handle case where res.data is sent instead of res
        const data = res.data || res;
        const batchTaskSuccessfullyCreated = res.success !== undefined ? res.success : (res.data !== undefined);
        this.loaderService.setProgress(data?.progress);
        const processInfolog: any[] = data?.processInfoLog;
        if (processInfolog) {
          for(; infologIndex < processInfolog.length; infologIndex++){//we want to display any new infologs. just the new ones
            const log = processInfolog[infologIndex];

            switch(log.infoType) {
              case 0: // Info          
                this.snackbar.info(log.text);
                break;
              case 1: // Warning
                this.snackbar.warning(log.text);
                break;
              case 2: // Error
                this.snackbar.error(log.text);
                break;
              case 3: // Success
                this.snackbar.success(log.text);
                break;
            }
          }
        }
        console.log(res.message ?? `Background task processing... `);
        if (!batchTaskSuccessfullyCreated) {
          this.snackbar.error(res.message || 'Task failed');
          console.error(res);
          throw new Error(`Task Failed. Reason: ${res.message}`);
        }
        
        // Check for completion - handle both capitalized and lowercase property names
        const status = (data.Status || data.status) ?? 0;
        const payload = data.Payload || data.payload;
        
        // BatchRunInfoStatus.Complete = 2 (from your C# code)
        
        if (status === 2) {
          //Successfully Completed
          if (!payload) {
            // console.log('pollForBatchTask: Task completed successfully, returning payload');
            console.log('pollForBatchTask: Status is Complete, no report attached');
          }

          succes = true;
          return payload??{};
        }
        else if (status === 3 || status === 4 || status === 5) {
          //Completed due to error or being terminated. Terminated should never happen
          //this.snackbar.error(res.message || `Background task failed with status: ${status}`); 
          console.error(res);
          throwTaskFailedError('Task Failed');
        }
        else {
          //Either it hasn't started or it is still running. Either way, we wait for a while and try again
          if(status === 0){
            //not running yet
            unsuccessfulPollCount++;
          }
          else if(status === 1){
            //still running
          }
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
      } catch (error) {
        console.error(`pollForBatchTask: Error on poll attempt #${unsuccessfulPollCount}:`, error);
        unsuccessfulPollCount++;
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
      finally{
        if (unsuccessfulPollCount >= maxRetries) {
          console.error(`Task did not initiate after ${maxRetries} attempts`);
          throwTaskFailedError('Task did not initiate');    
        }
      }
    }
    throwTaskFailedError('Task did not initiate');
  }

  async getPsychometricTestReport(resultData: any) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(
      `${env.apiURL}examination/report/psychometric?${resultData}`, { headers: this.configureHeaders() }
    ));
  }

  async getQuestionGroups(tableParam: TableParams) {
    return await lastValueFrom(this.httpClient.patch(
      `${env.apiURL}examination/question-groups`,
      tableParam, { headers: this.configureHeaders() }
    ), { defaultValue: [] });
  }

  async saveQuestionGroup(data: IQuestionGroup) {
    return this.httpClient.post(`${env.apiURL}examination/question-groups`, data, { headers: this.configureHeaders() }).toPromise();
  }

  async deleteQuestionGroup(data: IQuestionGroup) {
    return this.httpClient.delete(`${env.apiURL}examination/question-groups?id=${data.id}`, { headers: this.configureHeaders() }).toPromise();
  }

  async getQuestionGroupsLookup(examID: number): Promise<IQuestionGroup[]> {
    return this.httpClient
      .get<IQuestionGroup[]>(`${env.apiURL}examination/${examID}/question-groups-lookup`, {
        headers: this.configureHeaders()
      })
      .toPromise();
  }
  getExamScheduleCount(examID: any): Promise<any> {
    return this.httpClient.get(`${env.apiURL}examination/${examID}/schedule-count`, { headers: this.configureHeaders() }).toPromise();
  }

  requestMoreSaqScriptsForScoring(examId: number) {
    return lastValueFrom(
      this.httpClient.patch(
        `${env.apiURL}examination/${examId}/examiners/saq-scripts/request`,
        {},
        { headers: this.configureHeaders() }
      ),
      { defaultValue: [] }
    );
  }

  requestMoreSaqScriptsForReview(examId: number) {
    return lastValueFrom(
      this.httpClient.patch(
        `${env.apiURL}examination/${examId}/reviewers/saq-scripts/request`,
        {},
        { headers: this.configureHeaders() }
      ),
      { defaultValue: [] }
    );
  }

  requestMoreEssayScriptsForScoring(examId: number) {
    return lastValueFrom(
      this.httpClient.patch(
        `${env.apiURL}examination/${examId}/examiners/essay-scripts/request`,
        {},
        { headers: this.configureHeaders() }
      ),
      { defaultValue: [] }
    );
  }

  requestMoreEssayScriptsForReview(examId: number) {
    return lastValueFrom(
      this.httpClient.patch(
        `${env.apiURL}examination/${examId}/reviewers/essay-scripts/request`,
        {},
        { headers: this.configureHeaders() }
      ),
      { defaultValue: [] }
    );
  }

  async getExamResultPeriodReport(resultData: any) {
    return this.handleBackgroundTaskRequest(this.httpClient.get(`${env.apiURL}examination/report/result/period?${resultData}`, { headers: this.configureHeaders() }));
  }

}
